import React, { useEffect, useState, createContext, useContext } from 'react';
import { DarknessTier, ConsentFramework } from '../data/aftertideTypes';
export interface ReaderPreferences {
  maxTier: DarknessTier | null;
  excludedWarnings: string[];
  preferredConsent: ConsentFramework[];
}
export type ReadingStatus = 'currently-reading' | 'read' | 'dnf' | 'want-to-read';
export interface UserReview {
  id: string;
  bookId: string;
  rating: number;
  content: string;
  tierContext: string;
  date: string;
}
interface AfterTideUserContextType {
  cart: string[];
  wishlist: string[];
  preferences: ReaderPreferences;
  readingList: Record<string, ReadingStatus>;
  recentlyViewed: string[];
  reviews: UserReview[];
  addToCart: (bookId: string) => void;
  removeFromCart: (bookId: string) => void;
  clearCart: () => void;
  addToWishlist: (bookId: string) => void;
  removeFromWishlist: (bookId: string) => void;
  updatePreferences: (prefs: Partial<ReaderPreferences>) => void;
  setReadingStatus: (bookId: string, status: ReadingStatus | null) => void;
  getReadingStatus: (bookId: string) => ReadingStatus | null;
  trackView: (bookId: string) => void;
  addReview: (review: Omit<UserReview, 'id' | 'date'>) => void;
  getReviewForBook: (bookId: string) => UserReview | undefined;
}
const defaultPreferences: ReaderPreferences = {
  maxTier: null,
  excludedWarnings: [],
  preferredConsent: []
};
const AfterTideUserContext = createContext<AfterTideUserContextType | undefined>(undefined);
export const AfterTideUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [cart, setCart] = useState<string[]>(() => {
    const saved = localStorage.getItem('aftertide_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [wishlist, setWishlist] = useState<string[]>(() => {
    const saved = localStorage.getItem('aftertide_wishlist');
    return saved ? JSON.parse(saved) : [];
  });
  const [preferences, setPreferences] = useState<ReaderPreferences>(() => {
    const saved = localStorage.getItem('aftertide_preferences');
    return saved ? JSON.parse(saved) : defaultPreferences;
  });
  const [readingList, setReadingList] = useState<Record<string, ReadingStatus>>(() => {
    const saved = localStorage.getItem('aftertide_reading_list');
    return saved ? JSON.parse(saved) : {};
  });
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>(() => {
    const saved = localStorage.getItem('aftertide_recently_viewed');
    return saved ? JSON.parse(saved) : [];
  });
  const [reviews, setReviews] = useState<UserReview[]>(() => {
    const saved = localStorage.getItem('aftertide_reviews');
    return saved ? JSON.parse(saved) : [];
  });
  useEffect(() => {
    localStorage.setItem('aftertide_cart', JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem('aftertide_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);
  useEffect(() => {
    localStorage.setItem('aftertide_preferences', JSON.stringify(preferences));
  }, [preferences]);
  useEffect(() => {
    localStorage.setItem('aftertide_reading_list', JSON.stringify(readingList));
  }, [readingList]);
  useEffect(() => {
    localStorage.setItem('aftertide_recently_viewed', JSON.stringify(recentlyViewed));
  }, [recentlyViewed]);
  useEffect(() => {
    localStorage.setItem('aftertide_reviews', JSON.stringify(reviews));
  }, [reviews]);
  const addToCart = (bookId: string) => {
    setCart(prev => prev.includes(bookId) ? prev : [...prev, bookId]);
  };
  const removeFromCart = (bookId: string) => {
    setCart(prev => prev.filter(id => id !== bookId));
  };
  const clearCart = () => setCart([]);
  const addToWishlist = (bookId: string) => {
    setWishlist(prev => prev.includes(bookId) ? prev : [...prev, bookId]);
  };
  const removeFromWishlist = (bookId: string) => {
    setWishlist(prev => prev.filter(id => id !== bookId));
  };
  const updatePreferences = (prefs: Partial<ReaderPreferences>) => {
    setPreferences(prev => ({
      ...prev,
      ...prefs
    }));
  };
  const setReadingStatus = (bookId: string, status: ReadingStatus | null) => {
    setReadingList(prev => {
      const next = {
        ...prev
      };
      if (status === null) {
        delete next[bookId];
      } else {
        next[bookId] = status;
      }
      return next;
    });
  };
  const getReadingStatus = (bookId: string): ReadingStatus | null => {
    return readingList[bookId] || null;
  };
  const trackView = (bookId: string) => {
    setRecentlyViewed(prev => {
      const filtered = prev.filter(id => id !== bookId);
      return [bookId, ...filtered].slice(0, 20);
    });
  };
  const addReview = (review: Omit<UserReview, 'id' | 'date'>) => {
    const newReview: UserReview = {
      ...review,
      id: `user-${Date.now()}`,
      date: new Date().toISOString().split('T')[0]
    };
    setReviews(prev => {
      const filtered = prev.filter(r => r.bookId !== review.bookId);
      return [newReview, ...filtered];
    });
  };
  const getReviewForBook = (bookId: string) => {
    return reviews.find(r => r.bookId === bookId);
  };
  return <AfterTideUserContext.Provider value={{
    cart,
    wishlist,
    preferences,
    readingList,
    recentlyViewed,
    reviews,
    addToCart,
    removeFromCart,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    updatePreferences,
    setReadingStatus,
    getReadingStatus,
    trackView,
    addReview,
    getReviewForBook
  }} data-id="element-4121" data-mp="y49xiac0vo4a">
      {children}
    </AfterTideUserContext.Provider>;
};
export const useAfterTideUser = () => {
  const context = useContext(AfterTideUserContext);
  if (context === undefined) {
    throw new Error('useAfterTideUser must be used within an AfterTideUserProvider');
  }
  return context;
};